import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux";
import { getUsageReport, getMonthlyUsage, clearUsageData } from "../../redux/reducers/usageSlice";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { View, Text, DateField, Tooltip, Button } from "../base";
import { Icon } from "../base/Icon";

// Utility functions
const formatDuration = (minutes: number): string => {
  if (minutes === 0) return "0m";
  if (minutes < 1) return "< 1m";
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.floor(minutes % 60);
  if (hours === 0) return `${remainingMinutes}m`;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

export const UsageCard = () => {
  const dispatch = useAppDispatch();
  const { currentReport, isLoading, error } = useAppSelector((state) => state.usage);
  const { organizationId: effectiveOrganizationId } = useEffectiveOrganization();

  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDayOfMonth.toISOString().split("T")[0];
  });

  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split("T")[0];
  });

  const [selectedRange, setSelectedRange] = useState<string>("current-month");
  const [showCustomDates, setShowCustomDates] = useState(false);

  useEffect(() => {
    handleQuickRange("current-month");
    return () => {
      dispatch(clearUsageData());
    };
  }, [dispatch]);

  const handleQuickRange = (range: string) => {
    const now = new Date();
    setSelectedRange(range);
    setShowCustomDates(range === "custom");

    switch (range) {
      case "current-month": {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setStartDate(firstDay.toISOString().split("T")[0]);
        setEndDate(lastDay.toISOString().split("T")[0]);
        dispatch(
          getMonthlyUsage({
            year: now.getFullYear().toString(),
            month: (now.getMonth() + 1).toString(),
            organizationId: effectiveOrganizationId,
          }),
        );
        break;
      }
      case "last-month": {
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        setStartDate(lastMonthStart.toISOString().split("T")[0]);
        setEndDate(new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split("T")[0]);
        dispatch(
          getMonthlyUsage({
            year: lastMonthStart.getFullYear().toString(),
            month: (lastMonthStart.getMonth() + 1).toString(),
            organizationId: effectiveOrganizationId,
          }),
        );
        break;
      }
      case "last-7-days": {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        setStartDate(sevenDaysAgo.toISOString().split("T")[0]);
        setEndDate(now.toISOString().split("T")[0]);
        dispatch(
          getUsageReport({
            startDate: sevenDaysAgo.toISOString().split("T")[0],
            endDate: now.toISOString().split("T")[0],
            organizationId: effectiveOrganizationId,
          }),
        );
        break;
      }
      case "last-30-days": {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        setStartDate(thirtyDaysAgo.toISOString().split("T")[0]);
        setEndDate(now.toISOString().split("T")[0]);
        dispatch(
          getUsageReport({
            startDate: thirtyDaysAgo.toISOString().split("T")[0],
            endDate: now.toISOString().split("T")[0],
            organizationId: effectiveOrganizationId,
          }),
        );
        break;
      }
      case "custom":
        setShowCustomDates(true);
        break;
    }
  };

  const handleCustomDateRange = () => {
    if (startDate && endDate) {
      setSelectedRange("custom");
      dispatch(
        getUsageReport({
          startDate,
          endDate,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const quickRangeButtons = [
    { key: "current-month", label: "This Month" },
    { key: "last-month", label: "Last Month" },
    { key: "last-7-days", label: "7 Days" },
    { key: "last-30-days", label: "30 Days" },
    { key: "custom", label: "Custom" },
  ];

  // Computed values
  const totalSessions = currentReport?.usage?.totalSessions || 0;
  const totalDuration = currentReport?.usage?.totalDurationMinutes || 0;
  const avgSession = totalSessions > 0 ? totalDuration / totalSessions : 0;
  const activeAgents = currentReport?.usage?.agentBreakdown?.filter((a) => a.sessionCount > 0).length || 0;

  return (
    <View className="flex flex-col gap-5">
      {/* Date Range - compact inline controls */}
      <View className="flex flex-col gap-3">
        <View className="flex flex-row flex-wrap items-center gap-2">
          {quickRangeButtons.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handleQuickRange(key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedRange === key
                  ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </View>

        {/* Custom date inputs - inline, collapsible */}
        {showCustomDates && (
          <View className="flex flex-row items-center justify-start gap-2">
            <DateField
              value={startDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
              labelClassName="!w-36 !max-w-36 !text-sm"
              max={new Date().toISOString().split("T")[0]}
            />
            <Text className="text-xs text-gray-400 dark:text-gray-500">to</Text>
            <DateField
              value={endDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
              labelClassName="!w-36 !max-w-36 !text-sm"
              max={new Date().toISOString().split("T")[0]}
            />
            <Button
              variant="info"
              size="auto"
              onClick={handleCustomDateRange}
              disabled={!startDate || !endDate || isLoading}
            >
              Apply
            </Button>
          </View>
        )}
      </View>

      {/* Loading State */}
      {isLoading && (
        <View className="flex flex-col items-center justify-center py-16 gap-3">
          <View className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500" />
          <Text className="text-sm text-gray-400 dark:text-gray-500">Loading usage data...</Text>
        </View>
      )}

      {/* Error State */}
      {error && (
        <View className="flex flex-row items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
          <Icon
            name="info"
            size={16}
            className="text-red-500 shrink-0"
          />
          <Text className="text-sm text-red-600 dark:text-red-400">{error}</Text>
        </View>
      )}

      {/* Stats + Table */}
      {currentReport && !isLoading && (
        <>
          {/* Stat pills row - matching MissionControl style */}
          <View className="flex flex-row flex-wrap gap-3">
            <Tooltip
              content="Total number of voice sessions across all agents"
              placement="bottom"
            >
              <View className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-default">
                <Icon
                  name="phone"
                  size={14}
                  className="text-blue-500"
                />
                <Text className="text-sm font-semibold text-gray-900 dark:text-white">{totalSessions.toLocaleString()}</Text>
                <Text className="text-xs text-gray-400 dark:text-gray-500">sessions</Text>
              </View>
            </Tooltip>
            <Tooltip
              content="Combined duration of all voice sessions"
              placement="bottom"
            >
              <View className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-default">
                <Icon
                  name="clock"
                  size={14}
                  className="text-emerald-500"
                />
                <Text className="text-sm font-semibold text-gray-900 dark:text-white">{formatDuration(totalDuration)}</Text>
                <Text className="text-xs text-gray-400 dark:text-gray-500">total</Text>
              </View>
            </Tooltip>
            <Tooltip
              content="Average length of a single session"
              placement="bottom"
            >
              <View className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-default">
                <Icon
                  name="trending-up"
                  size={14}
                  className="text-purple-500"
                />
                <Text className="text-sm font-semibold text-gray-900 dark:text-white">{formatDuration(avgSession)}</Text>
                <Text className="text-xs text-gray-400 dark:text-gray-500">avg</Text>
              </View>
            </Tooltip>
            <Tooltip
              content="Number of agents with at least one session"
              placement="bottom"
            >
              <View className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-default">
                <Icon
                  name="bot"
                  size={14}
                  className="text-amber-500"
                />
                <Text className="text-sm font-semibold text-gray-900 dark:text-white">{activeAgents}</Text>
                <Text className="text-xs text-gray-400 dark:text-gray-500">active agents</Text>
              </View>
            </Tooltip>
          </View>

          {/* Agent Breakdown - modern list style */}
          <View className="flex flex-col">
            <View className="flex flex-row items-center justify-between mb-3">
              <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Breakdown by Agent</Text>
              {currentReport.usage.agentBreakdown && currentReport.usage.agentBreakdown.length > 0 && (
                <Text className="text-xs text-gray-400 dark:text-gray-500">
                  {currentReport.usage.agentBreakdown.length} {currentReport.usage.agentBreakdown.length === 1 ? "agent" : "agents"}
                </Text>
              )}
            </View>

            {currentReport.usage.agentBreakdown && currentReport.usage.agentBreakdown.length > 0 ? (
              <View className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700/50 overflow-hidden">
                {/* Table header */}
                <View className="flex flex-row items-center px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700/50">
                  <Text className="flex-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Agent</Text>
                  <Text className="w-24 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Sessions</Text>
                  <Text className="w-24 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Duration</Text>
                  <Text className="w-20 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right hidden sm:block">Avg</Text>
                  <Text className="w-32 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right hidden md:block">Share</Text>
                </View>

                {/* Table rows */}
                {currentReport.usage.agentBreakdown
                  .slice()
                  .sort((a, b) => b.durationMinutes - a.durationMinutes)
                  .map((agent, index) => {
                    const percentOfTotal =
                      currentReport.usage.totalDurationMinutes > 0 ? (agent.durationMinutes / currentReport.usage.totalDurationMinutes) * 100 : 0;
                    const avgSessionDuration = agent.sessionCount > 0 ? agent.durationMinutes / agent.sessionCount : 0;

                    return (
                      <View
                        key={agent.agentId}
                        className={`flex flex-row items-center px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/30 ${
                          index < currentReport.usage.agentBreakdown.length - 1 ? "border-b border-gray-100 dark:border-gray-800" : ""
                        }`}
                      >
                        {/* Agent name */}
                        <View className="flex flex-row items-center gap-2.5 flex-1 min-w-0">
                          <View className="flex items-center justify-center w-7 h-7 rounded-md bg-gray-100 dark:bg-gray-800 shrink-0">
                            <Icon
                              name="bot"
                              size={14}
                              className="text-gray-400 dark:text-gray-500"
                            />
                          </View>
                          <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">{agent.agentName}</Text>
                        </View>

                        {/* Sessions */}
                        <Text className="w-24 text-sm tabular-nums text-gray-900 dark:text-white text-right font-medium">
                          {agent.sessionCount.toLocaleString()}
                        </Text>

                        {/* Duration */}
                        <Text className="w-24 text-sm tabular-nums text-gray-900 dark:text-white text-right">{formatDuration(agent.durationMinutes)}</Text>

                        {/* Avg */}
                        <Text className="w-20 text-sm tabular-nums text-gray-500 dark:text-gray-400 text-right hidden sm:block">
                          {formatDuration(avgSessionDuration)}
                        </Text>

                        {/* Share bar */}
                        <View className="w-32 flex flex-row items-center gap-2 justify-end hidden md:flex">
                          <View className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                            <View
                              className="bg-blue-500 dark:bg-blue-400 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(percentOfTotal, 100)}%` }}
                            />
                          </View>
                          <Text className="text-xs tabular-nums text-gray-400 dark:text-gray-500 w-10 text-right">{percentOfTotal.toFixed(1)}%</Text>
                        </View>
                      </View>
                    );
                  })}
              </View>
            ) : (
              <View className="flex flex-col items-center justify-center py-12 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
                <Icon
                  name="activity"
                  size={28}
                  className="text-gray-300 dark:text-gray-600 mb-3"
                />
                <Text className="text-sm text-gray-500 dark:text-gray-400 text-center">No agent activity for this period.</Text>
                <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1">Try selecting a different time range.</Text>
              </View>
            )}
          </View>
        </>
      )}
    </View>
  );
};
